<template>
  <base-card>
    <div class="grid sm:grid-cols-3 items-start gap-4">
      <base-input
        fullwidth
        inset
        label="Catatan"
        tag="textarea"
        rows="3"
        placeholder="Masukkan catatan"
        v-model="order.notes"
        @change="onChangeNotes"
      />
      <div class="grid grid-cols-2 gap-4">
        <base-input
          inset
          class="col-span-full"
          disabled
          label="Total Barang"
          :value="totalQty | toCurrency"
        />
        <template v-if="order.discountType">
          <base-input
            v-if="order.discountType === 'non_bv'"
            inset
            class="col-span-full"
            disabled
            label="Potongan"
            :value="order.discountPrice | toCurrency"
          />
          <div v-else class="col-span-full grid grid-cols-2 gap-4">
            <base-input
              inset
              label="Diskon(%)"
              placeholder="0"
              currency
              type="text"
              v-model="order.discountPriceInput"
              @change="onChangeDiscountPrice"
            />
            <base-input
              inset
              disabled
              label="Potongan"
              :value="order.discountPrice | toCurrency"
            />
          </div>
        </template>
        <base-input
          v-if="withCashback"
          inset
          class="col-span-full"
          disabled
          label="Cashback"
          :value="cashback | toCurrency"
        />
        <template v-if="order.originOfficeType === 'pusat' && order.shipment_type === 'delivery'">
          <base-input
            inset
            disabled
            label="Selisih"
            :value="order.shippingFee | toCurrency"
          />
          <base-input
            inset
            disabled
            label="Plastik"
            :value="order.plasticQty | toCurrency"
          />
        </template>

        <div class="space-y-1">
          <p class="text-xs text-gray-500">Total Pembayaran</p>
          <p class="text-2xl font-bold">{{ totalNeedPayment | toCurrency }}</p>
        </div>
      </div>
      <div class="grid gap-4">
        <template v-if="withTax">
          <base-input inset disabled label="DPP" :value="dpp | toCurrency" />
          <base-input inset disabled label="PPN (%)" :value="ppn | toCurrency" />
        </template>
        <base-input
          inset
          disabled
          label="Total Belanja"
          :value="subTotalPrice | toCurrency"
        />
        <base-input
          inset
          disabled
          :label="paymentRemainder > 0 ? 'Total Kekurangan' : 'Total Kelebihan'"
          :value="Math.abs(paymentRemainder) | toCurrency"
        />
      </div>
    </div>

    <loading v-if="loadingSaveNotes || loadingSaveDiscountPrice" />
  </base-card>
</template>

<script>
import { requestMixin } from '../../mixins/request/request';

export default {
  mixins: [requestMixin],
  props: {
    value: Object,
    orderDetails: Array,
    cashback: Number,
    paymentMethods: Array,
    paymentType: {
      type: String,
      default: 'payment-methods'
    },
    withCashback: {
      type: Boolean,
      default: true
    },
    withTax: {
      type: Boolean,
      default: true
    },
    totalPaymentAmount: Number
  },
  emits: ['input'],
  data() {
    return {
      loadingSaveNotes: false,
      loadingSaveDiscountPrice: false
    };
  },
  computed: {
    order: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    totalQty() {
      return this.orderDetails.reduce(
        (total, orderDetail) => total + Number(orderDetail.qty),
        0
      );
    },
    dpp() {
      return (100 / 111) * this.totalPrice;
    },
    ppn() {
      return (11 / 100) * this.dpp;
    },
    subTotalPrice() {
      return this.orderDetails.reduce(
        (total, orderDetail) => total + orderDetail.totalPrice,
        0
      );
    },
    totalPrice() {
      if (!this.order.customPrice || !this.order.discountType) {
        return this.subTotalPrice
      }

      return this.subTotalPrice - this.order.discountPrice
    },
    totalPayment() {
      if (typeof this.totalPaymentAmount === 'number') {
        return this.totalPaymentAmount
      }
      if (this.paymentType === 'payment-methods') {
        return this.paymentMethods
          .filter((paymentMethod) => {
            return (
              paymentMethod.amount &&
              !isNaN(Number(paymentMethod.amount.replace(/\D/gi, '')))
            );
          })
          .reduce(
            (total, paymentMethod) =>
              total + Number(paymentMethod.amount.replace(/\D/gi, '')),
            0
          );
      }

      return this.paymentMethods.reduce(
        (total, item) => total + (typeof item.amount === 'string' ? parseInt(item.amount.replace(/\D/gi, '')) : item.amount),
        0
      );
    },
    paymentRemainder() {
      return this.totalPrice - (this.withCashback ? this.cashback : 0) - this.totalPayment;
    },
    totalNeedPayment() {
      return this.totalPrice - (this.withCashback ? this.cashback : 0)
    }
  },
  methods: {
    async onChangeNotes() {
      this.loadingSaveNotes = true;

      await this.request(`/api/v1/orders/${this.order.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'orders',
            id: this.order.id,
            attributes: {
              notes: this.order.notes,
            },
          },
        },
      });

      this.loadingSaveNotes = false;
    },
    async onChangeDiscountPrice() {
      const input = !this.order.discountPriceInput ? 0 : Number(this.order.discountPriceInput.replace(/\D/gi, ''))

      if (input < 1 || input > 100) {
        this.order.discountPriceInput = this.order.discountPriceValue

        return
      }

      this.loadingSaveDiscountPrice = true;

      const [res, err] = await this.request(`/api/v1/orders/${this.order.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'orders',
            id: this.order.id,
            attributes: {
              discount_price: input,
            },
          },
        },
      });

      if (err) {
        this.order.discountPriceInput = this.order.discountPriceValue
      } else {
        this.order.discountPriceValue = this.order.discountPriceInput
        this.order.discountPrice = res.data.attributes.discount_price
      }

      this.loadingSaveDiscountPrice = false;
    }
  },
};
</script>
